import React from 'react';
import { useSearchParams } from 'react-router-dom';
import TwitchLink from './components/twitchLink';
import { prizeWithAmount } from './components/prizeIcon';
import { TorneosBrowser } from './torneos';

// Ligas y torneos de los juegos: la pagina arranca con los dos botones y cada uno abre lo suyo
// (los torneos son los mismos de /info/torneos, ver torneos.jsx).
//
// Clasificaciones de las ligas.
//
// La jerarquia es juego -> subliga -> temporada. Un juego puede tener varias subligas (por
// ejemplo "Balon Brawl" dentro de Brawl Stars); la que no tiene subliga se llama "General".
// La fila de subligas solo aparece si el juego tiene alguna ademas de la general.

// Desplegable de temporada.
//
// No es un <select>: el navegador pinta la lista de un select por su cuenta e ignora el
// padding y el line-height de las <option>, asi que las temporadas salian pegadas y no habia
// forma de separarlas. Con divs el CSS manda (ver .seasonPicker en App.css).
const SeasonPicker = ({ season, value, onChange }) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const options = ['All'].concat(Array.from({ length: season }, (_, i) => String(i + 1)));
  const label = (v) => (v === 'All' ? 'Todas' : v);

  return (
    <span className='seasonPicker' ref={ref}>
      <span
        className='seasonValue'
        role='button'
        tabIndex={0}
        aria-haspopup='listbox'
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(o => !o);
          }
        }}
      >
        {label(value)}
      </span>
      {open ? (
        <ul className='seasonList' role='listbox'>
          {options.map(opt => (
            <li
              key={opt}
              role='option'
              aria-selected={opt === String(value)}
              className={opt === String(value) ? 'seasonSel' : ''}
              onClick={() => { onChange(opt); setOpen(false); }}
            >
              {label(opt)}
            </li>
          ))}
        </ul>
      ) : null}
    </span>
  );
};

const Games = () => {

  const [section, setSection] = React.useState(''); // 'ligas' | 'torneos'

  const [leagues, setLeagues] = React.useState([]);
  const [gamers, setGamers] = React.useState([]);
  const [gameSel, setGameSel] = React.useState('');
  const [subSel, setSubSel] = React.useState('');
  const [seasonSel, setSeasonSel] = React.useState('All');

  // Enlace desde la portada: /games?league=<id> abre directamente esa liga.
  const [searchParams] = useSearchParams();
  const leagueParam = searchParams.get('league');

  React.useEffect(() => {
    getLeagues();
  }, []);

  React.useEffect(() => {
    if (!leagueParam || leagues.length === 0) return;
    const league = leagues.find(l => String(l._id) === leagueParam);
    if (!league) return;
    setSection('ligas');
    setGameSel(league.name);
    setSubSel(league.subLeague);
    // Se entra por la temporada en curso, que es la que se está jugando; el histórico ('Todas')
    // queda a un clic en el selector.
    setSeasonSel(String(league.season));
  }, [leagueParam, leagues]);

  const getLeagues = async () => {
    let list = await fetch('/api/games', { method: 'POST' });
    setLeagues(await list.json());
  };

  // Un juego = todas sus subligas. Se conserva el orden que trae el backend (General primero).
  const byGame = leagues.reduce((acc, league) => {
    (acc[league.name] = acc[league.name] || []).push(league);
    return acc;
  }, {});
  const gameNames = Object.keys(byGame);

  const subLeagues = gameSel ? (byGame[gameSel] || []) : [];
  const selected = subLeagues.find(l => l.subLeague === subSel) || null;

  const loadTable = React.useCallback(async (league, season) => {
    if (!league) {
      setGamers([]);
      return;
    }
    let list = await fetch('/api/games?league=' + league._id + '&season=' + season, { method: 'POST' });
    setGamers(await list.json());
  }, []);

  React.useEffect(() => {
    loadTable(selected, seasonSel);
  }, [selected, seasonSel, loadTable]);

  const changeGame = (name) => {
    setGameSel(name);
    // Al cambiar de juego se entra siempre por su liga general.
    const first = (byGame[name] || [])[0];
    setSubSel(first ? first.subLeague : '');
    setSeasonSel('All');
  };

  const changeSub = (subLeague) => {
    setSubSel(subLeague);
    setSeasonSel('All');
  };

  return (

    <div className="topTickets page">
      <div className='navTops heroes unselectable'>
        <div className={section === 'ligas' ? 'active' : ''} onClick={() => setSection('ligas')}>Ligas</div>
        <div className={section === 'torneos' ? 'active' : ''} onClick={() => setSection('torneos')}>Torneos</div>
      </div>

      {section === 'ligas' ? (<>
        <h3 className='heroes navTitle'>Juegos</h3>
        <div className='navTops heroes unselectable'>
          {gameNames.map(name => (
            <div key={name} className={name === gameSel ? 'active' : ''} onClick={() => changeGame(name)}>
              {/* Con imagen configurada el logo sustituye al nombre (que queda como alt). */}
              {byGame[name][0].image
                ? <img className='gameImg' src={byGame[name][0].image} alt={name.replace(/_/g, ' ')} />
                : name.replace(/_/g, ' ')}
            </div>
          ))}
        </div>

        {subLeagues.length > 1 ? (
          <>
          <h3 className='heroes navTitle'>Ligas</h3>
          <div className='navTops heroes unselectable'>
            {subLeagues.map(league => (
              <div
                key={league._id}
                className={league.subLeague === subSel ? 'active' : ''}
                onClick={() => changeSub(league.subLeague)}
              >
                {league.subLeague}
              </div>
            ))}
          </div>
          </>
        ) : null}

        {selected ? (
          <div className='navTops heroes selector'>
            <div className='active'>
              <span className='unselectable'>
                Temporada:
                <SeasonPicker season={selected.season} value={seasonSel} onChange={setSeasonSel} />
              </span>
            </div>
          </div>
        ) : null}

        <div className='navTops'>
          <table id="gamers" className="heroes">
            <tbody>
              <tr className="trtitulo">
                <td>#</td>
                <td>Nombre de Participantes</td>
                <td>Victorias</td>
                <td>Premio</td>
              </tr>
              {gamers.map(user => (
                <tr key={user.userId}>
                  {/* El puesto lo calcula el servidor: los empatados a victorias lo comparten. */}
                  <td>{user.position}</td>
                  <td>
                    <div>
                      <span>{user.displayname}</span>
                    </div>
                    <TwitchLink displayname={user.displayname} usert={user.usert} useryt={user.useryt} />
                  </td>
                  <td>{user.totalPoints}</td>
                  <td className='prizeCell'>{prizeWithAmount(user.prize)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>) : null}

      {section === 'torneos' ? <TorneosBrowser /> : null}
    </div>
  )
}

export default Games;
